import './styles.scss';

import {
  FocusEvent,
  ForwardedRef,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import Input from '@/common/Input';
import { PopoverInputTypes } from '@/common/PopoverInput/types';
import Popup from '@/common/Popup';
import { getClasses } from '@/utils';
import useOnClickOutside from '@/hooks/useOnClickOutside';

const PopoverInput = forwardRef<PopoverInputTypes.RefType, PopoverInputTypes.Props>(
  (
    { children, placement = 'bottom', onShow, onHide, hideOnEnter = true, ...props }: PopoverInputTypes.Props,
    ref: ForwardedRef<PopoverInputTypes.RefType>
  ): ReactElement => {
    const inputRef = useRef<HTMLInputElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);

    const [focus, setFocus] = useState<boolean>(false);

    useOnClickOutside(
      popoverRef,
      (): void => {
        setFocus(false);
        onHide?.();
      },
      'mouseup'
    );
    useImperativeHandle(ref, () => {
      const input = inputRef.current;
      return input
        ? {
            ...input,
            focus: (): void => {
              setFocus(true);
              onShow?.();
              input.focus();
            },
            blur: (): void => {
              setFocus(false);
              onHide?.();
              input.blur();
            },
            show: (): void => {
              setFocus(true);
              onShow?.();
            },
            hide: (): void => {
              setFocus(false);
              onHide?.();
            },
            target: input,
          }
        : ({} as any);
    }, [onShow, onHide]);

    return (
      <>
        <Input
          {...props}
          className={getClasses('PopoverInput', 'Common', props.className)}
          onFocus={(value: string, event: FocusEvent<HTMLInputElement>): void => {
            setFocus(true);
            props.onFocus?.(value, event);
            onShow?.();
          }}
          onClick={(value: string, event: MouseEvent<HTMLInputElement>): void => {
            setFocus(true);
            props.onClick?.(value, event);
            onShow?.();
          }}
          onEnter={(value: string, event: KeyboardEvent<HTMLInputElement>): void => {
            props.onEnter?.(value, event);
            props.onKeyDown?.(value, event);
            if (!hideOnEnter) return;
            setFocus(false);
            onHide?.();
          }}
          onTab={(value: string, event: KeyboardEvent<HTMLInputElement>): void => {
            setFocus(false);
            props.onTab?.(value, event);
            props.onKeyDown?.(value, event);
            onHide?.();
          }}
          ref={inputRef}
        />
        <Popup.Popover
          className="PopoverInput-Popover"
          show={focus}
          onHide={() => {
            setFocus(false);
            onHide?.();
          }}
          placement={placement}
          target={inputRef.current}
          role="menu"
          ref={popoverRef}
        >
          {children}
        </Popup.Popover>
      </>
    );
  }
);
PopoverInput.displayName = 'PopoverInput';

export default PopoverInput;
